(() => {
  'use strict';

  /*
   * Full Cycle cart dispatch estimator
   * ----------------------------------
   * fulfillment_days is the number of BUSINESS days added to the processing date.
   * A 0-day item ordered before the cutoff can dispatch today.
   * At or after the cutoff, the processing date rolls to the next business day.
   *
   * Store operating timezone: Ottawa/Toronto.
   * Change CUTOFF_HOUR / CUTOFF_MINUTE here if your dispatch cutoff changes.
   */
  const STORE_TIME_ZONE = 'America/Toronto';
  const CUTOFF_HOUR = 12; // 12:00 PM
  const CUTOFF_MINUTE = 0;
  const CART_ATTRIBUTE_NAME = 'Estimated dispatch';

  const monthNameFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const weekdayFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    weekday: 'long'
  });

  function dateKey(date) {
    return date.toISOString().slice(0, 10);
  }

  function makeDate(year, monthIndex, day) {
    return new Date(Date.UTC(year, monthIndex, day, 12, 0, 0));
  }

  function addDays(date, days) {
    const next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  }

  function nthWeekdayOfMonth(year, monthIndex, weekday, nth) {
    const first = makeDate(year, monthIndex, 1);
    const offset = (weekday - first.getUTCDay() + 7) % 7;
    return makeDate(year, monthIndex, 1 + offset + (nth - 1) * 7);
  }

  function victoriaDay(year) {
    const may24 = makeDate(year, 4, 24);
    const day = may24.getUTCDay();
    const daysBack = day === 1 ? 7 : (day + 6) % 7;
    return addDays(may24, -daysBack);
  }

  function easterSunday(year) {
    // Gregorian computus (Meeus/Jones/Butcher algorithm).
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return makeDate(year, month - 1, day);
  }

  function observedDate(date) {
    const weekday = date.getUTCDay();
    if (weekday === 6) return addDays(date, 2); // Saturday -> Monday
    if (weekday === 0) return addDays(date, 1); // Sunday -> Monday
    return date;
  }

  function holidayKeysForYear(year) {
    const holidays = new Set();
    const add = (date) => holidays.add(dateKey(date));

    // Common Ontario business closures.
    add(observedDate(makeDate(year, 0, 1)));                     // New Year's Day
    add(nthWeekdayOfMonth(year, 1, 1, 3));                      // Family Day
    add(addDays(easterSunday(year), -2));                        // Good Friday
    add(victoriaDay(year));                                      // Victoria Day
    add(observedDate(makeDate(year, 6, 1)));                     // Canada Day
    add(nthWeekdayOfMonth(year, 7, 1, 1));                       // Civic Holiday
    add(nthWeekdayOfMonth(year, 8, 1, 1));                       // Labour Day
    add(nthWeekdayOfMonth(year, 9, 1, 2));                       // Thanksgiving

    const christmas = makeDate(year, 11, 25);
    const boxingDay = makeDate(year, 11, 26);
    const christmasWeekday = christmas.getUTCDay();
    const boxingWeekday = boxingDay.getUTCDay();

    // Christmas / Boxing Day observed dates without collisions.
    if (christmasWeekday === 6) {
      add(makeDate(year, 11, 27)); // Mon
      add(makeDate(year, 11, 28)); // Tue
    } else if (christmasWeekday === 0) {
      add(makeDate(year, 11, 27)); // Tue for Christmas
      add(makeDate(year, 11, 26)); // Mon for Boxing Day
    } else if (boxingWeekday === 6) {
      add(christmas);
      add(makeDate(year, 11, 28));
    } else if (boxingWeekday === 0) {
      add(christmas);
      add(makeDate(year, 11, 27));
    } else {
      add(christmas);
      add(boxingDay);
    }

    return holidays;
  }

  const holidayCache = new Map();
  function isHoliday(date) {
    const year = date.getUTCFullYear();
    if (!holidayCache.has(year)) {
      holidayCache.set(year, holidayKeysForYear(year));
    }
    return holidayCache.get(year).has(dateKey(date));
  }

  function isBusinessDay(date) {
    const weekday = date.getUTCDay();
    return weekday !== 0 && weekday !== 6 && !isHoliday(date);
  }

  function getTorontoNowParts() {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: STORE_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    });

    const parts = Object.fromEntries(
      formatter.formatToParts(new Date())
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute)
    };
  }

  function nextBusinessDay(date) {
    let candidate = addDays(date, 1);
    while (!isBusinessDay(candidate)) candidate = addDays(candidate, 1);
    return candidate;
  }

  function calculateDispatchDate(fulfillmentDays) {
    const now = getTorontoNowParts();
    let cursor = makeDate(now.year, now.month - 1, now.day);
    const afterCutoff = now.hour > CUTOFF_HOUR ||
      (now.hour === CUTOFF_HOUR && now.minute >= CUTOFF_MINUTE);

    // If today is not a business day or the cutoff has passed, start from the
    // next business day. A fulfillment value of 0 therefore means today before
    // noon, or the next business day at/after noon.
    if (!isBusinessDay(cursor) || afterCutoff) {
      cursor = nextBusinessDay(cursor);
    }

    let remaining = Math.max(0, fulfillmentDays);
    while (remaining > 0) {
      cursor = nextBusinessDay(cursor);
      remaining -= 1;
    }

    return { date: cursor, afterCutoff };
  }

  function formatDispatchDate(date) {
    return `${weekdayFormatter.format(date)}, ${monthNameFormatter.format(date)}`;
  }

  function buildDetail(days, afterCutoff) {
    const cutoffText = '12:00 PM ET';
    const dayText = `${days} business ${days === 1 ? 'day' : 'days'}`;
    if (afterCutoff) {
      return `Longest item fulfillment time: ${dayText}. Orders placed after ${cutoffText} start processing the next business day.`;
    }
    return `Longest item fulfillment time: ${dayText}. Order before ${cutoffText} for today to count as the first processing day.`;
  }

  let lastSavedAttribute = null;
  let saveTimer = null;

  function persistEstimateToCart(value) {
    if (!value || value === lastSavedAttribute) return;
    lastSavedAttribute = value;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: { [CART_ATTRIBUTE_NAME]: value } })
      }).catch(() => {
        // The cart display still works even if the non-critical attribute write fails.
      });
    }, 250);
  }

  function updateEstimates() {
    const estimates = document.querySelectorAll('[data-fulfillment-estimate]');
    if (!estimates.length) return;

    let persistedValue = '';

    estimates.forEach((element) => {
      let days = Number.parseInt(element.dataset.fulfillmentDays || '0', 10);
      if (!Number.isFinite(days) || days < 0) days = 0;

      const result = calculateDispatchDate(days);
      const formattedDate = formatDispatchDate(result.date);
      const dateElement = element.querySelector('[data-fulfillment-estimate-date]');
      const detailElement = element.querySelector('[data-fulfillment-estimate-detail]');

      const dateText = `Expected to dispatch by ${formattedDate}`;
      const detailText = buildDetail(days, result.afterCutoff);
      if (dateElement && dateElement.textContent !== dateText) dateElement.textContent = dateText;
      if (detailElement && detailElement.textContent !== detailText) detailElement.textContent = detailText;

      persistedValue = `By ${formattedDate} (${days} business ${days === 1 ? 'day' : 'days'})`;
    });

    if (persistedValue) persistEstimateToCart(persistedValue);
  }

  let mutationTimer = null;
  function scheduleUpdate() {
    clearTimeout(mutationTimer);
    mutationTimer = setTimeout(updateEstimates, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateEstimates, { once: true });
  } else {
    updateEstimates();
  }

  new MutationObserver(scheduleUpdate).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
